import { create } from 'zustand'

export type PublishValidationErrorT = {
  field: 'recipient' | 'status'
  message: string
}

type NewIssueDialogStoreT = {
  dialogOpen: boolean
  toggleDialog: () => void
  updateDialogState: (dialogOpen: boolean) => void
  draftIssueId: string | null
  setDraftIssueId: (id: string | null) => void
  addingIssueInProgress: boolean
  setAddingIssueInProgress: (addingIssueInProgress: boolean) => void
  // "After saving, add another" toggle. Promoted from local useState in the
  // footer so the mobile top-bar icon-toggle and the desktop footer switch
  // share the same source of truth. The submit hook also reads this to
  // decide whether to closeDialog() or just clear title/description.
  addMoreIssuesAfterSave: boolean
  setAddMoreIssuesAfterSave: (v: boolean) => void
  // Publish-gate errors (recipient/status required by the issue type). Kept
  // here rather than in RHF formState: `setError` on these never-registered,
  // popover-driven fields proved unreliable at re-rendering the dialog's
  // portal subtree (a blocked publish showed no feedback), while a store
  // subscription notifies the alert/chips directly.
  publishValidationErrors: PublishValidationErrorT[]
  setPublishValidationErrors: (errors: PublishValidationErrorT[]) => void
  clearPublishValidationError: (field: PublishValidationErrorT['field']) => void
}

export const useNewIssueDialogStore = create<NewIssueDialogStoreT>(set => ({
  dialogOpen: false,
  updateDialogState: dialogOpen =>
    set(state => {
      if (dialogOpen) state.setAddingIssueInProgress(true)
      return { dialogOpen }
    }),
  draftIssueId: null,
  setDraftIssueId: draftIssueId => set({ draftIssueId }),
  addingIssueInProgress: false,
  setAddingIssueInProgress: addingIssueInProgress => set({ addingIssueInProgress }),
  addMoreIssuesAfterSave: false,
  setAddMoreIssuesAfterSave: addMoreIssuesAfterSave => set({ addMoreIssuesAfterSave }),
  publishValidationErrors: [],
  setPublishValidationErrors: publishValidationErrors => set({ publishValidationErrors }),
  clearPublishValidationError: field =>
    set(state =>
      state.publishValidationErrors.some(e => e.field === field)
        ? { publishValidationErrors: state.publishValidationErrors.filter(e => e.field !== field) }
        : state,
    ),
  toggleDialog: () =>
    set(state => {
      return { dialogOpen: !state.dialogOpen }
    }),
}))
